// Shared invoice/estimate-draft construction. One source of truth for what
// an invoice looks like, so the Money workspace's one-click invoicing and
// EstimatePanel's builder can't produce different dollar figures — both
// price through EstimatePanel.calcTotals.
//
// Kept out of EstimatePanel (a large component file) so non-UI callers can
// import it without pulling the whole panel; it imports only the pure
// pricing helpers back from EstimatePanel.
import { calcTotals, emptyEstimateLine } from '../../views/EstimatePanel';
import { activeEstimate } from './estimateMoney';
import { nowSeconds } from '../events/operationEvents';
import { getCurrentRole } from '../../shared/permissions';

function makeId(prefix = 'el') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

// A blank estimate scaffold for a truck — the pure equivalent of
// EstimatePanel's makeNew(): complaint from the RO, cause/correction from
// the latest finding/repair, every part on the RO included by default.
// `termsDays` is the customer's own Net terms when the caller knows them; the
// shop default is only a fallback. Terms are STAMPED on the estimate here and
// never re-read, so changing a customer's terms later can't silently re-date an
// invoice they already hold.
export function makeEstimateDraft(truck, settings, { termsDays = null } = {}) {
  const activities = truck.activities || [];
  const findings = activities.filter(a => a.type === 'finding' && a.text);
  const repairs  = activities.filter(a => a.type === 'repair'  && a.text);
  return {
    id: makeId('est'), truckId: truck.id, roNumber: truck.repairOrder,
    status: 'draft',
    lines: [{
      title:       '',
      complaint:   truck.reason || '',
      cause:       findings[0]?.text || '',
      correction:  repairs[0]?.text  || '',
      laborLines:  [],
      partLineIds: (truck.parts || []).map(p => p.id),
      taxExempt:   false,
    }],
    shopSupplies: 0, shopSuppliesPct: null, taxRate: settings?.pricing?.taxRate ?? 0,
    subtotal: 0, tax: 0, total: 0,
    notes: '', customerNotes: '', token: null,
    sentAt: null, approvedAt: null, approvalMethod: null,
    approvedBy: '', approvalNotes: '', declinedAt: null, declineReason: '',
    invoicedAt: null, lockedAt: null,
    paymentTermsDays: termsDays ?? settings?.pricing?.paymentTermsDays ?? 30,
    createdBy: getCurrentRole(), createdAt: nowSeconds(), updatedAt: nowSeconds(), version: 1,
  };
}

// Labor lines derived from the RO's repair categories (estimatedTime × the
// shop's default rate) — used only when a job reached "done" with no labor
// ever entered, so an invoice still reflects the hours planned.
function laborFromCategories(truck, settings) {
  const rate = num(settings?.pricing?.defaultLaborRate ?? 95) || 95;
  return (truck.repairCategories || [])
    .filter(c => num(c.estimatedTime) > 0)
    .map(c => {
      const hours = num(c.estimatedTime);
      return {
        id: makeId('lab'),
        description: [c.category, c.subcategory].filter(Boolean).join(' — ') || 'Labor',
        catalogItemId: null,
        hours,
        rate,
        flatPrice: null,
        amount: hours * rate,
        techName: null,
        notes: 'Auto-added from planned repair categories',
      };
    });
}

// Build an invoice-ready draft for a truck WITHOUT opening the estimate
// builder. Clones the active estimate when one exists (preserving entered
// labor); otherwise scaffolds a fresh one and fills labor from the repair
// categories. Prices via the shared calcTotals, then stamps it invoiced.
//
// Returns { draft, laborHours, hasEstimate, derivedLabor } so the caller can
// show the computed total before committing.
export function buildInvoiceDraft(truck, settings, { termsDays = null } = {}) {
  const existing = activeEstimate(truck);
  const hasEstimate = !!existing;
  const base = hasEstimate
    ? JSON.parse(JSON.stringify(existing))
    : makeEstimateDraft(truck, settings, { termsDays });

  // Ensure at least one operation line with the RO's parts attached.
  if (!base.lines || base.lines.length === 0) {
    base.lines = [{ ...emptyEstimateLine(), partLineIds: (truck.parts || []).map(p => p.id) }];
  }

  // No labor anywhere yet → derive it from planned categories onto line 0.
  const totalLaborLines = base.lines.reduce((n, l) => n + (l.laborLines?.length || 0), 0);
  let derivedLabor = false;
  if (totalLaborLines === 0) {
    const derived = laborFromCategories(truck, settings);
    if (derived.length) {
      base.lines[0].laborLines = derived;
      derivedLabor = true;
    }
  }

  const totals = calcTotals(base, truck.parts || []);
  const ts = nowSeconds();
  const draft = {
    ...base,
    status: 'invoiced',
    invoicedAt: base.invoicedAt || ts,
    paymentTermsDays: base.paymentTermsDays || termsDays || settings?.pricing?.paymentTermsDays || 30,
    subtotal: totals.subtotal,
    tax: totals.tax,
    total: totals.total,
    updatedAt: ts,
  };

  const laborHours = base.lines.reduce(
    (n, l) => n + (l.laborLines || []).reduce((s, x) => s + num(x.hours), 0), 0);

  return { draft, laborHours, hasEstimate, derivedLabor, total: totals.total };
}

// Adds labor lines and part references onto the RO's working estimate (creating
// one if there isn't a draft yet), then reprices through calcTotals so the
// money rail shows a real number instead of $0.
//
// Only touches a draft. An estimate that has already been sent, approved, or
// invoiced is left alone and a fresh draft is started — you don't get to edit
// a number the customer already agreed to.
export function mergeLinesIntoEstimate(truck, settings, { laborLines = [], newParts = [], complaint, cause, correction, termsDays = null }) {
  const existing = activeEstimate(truck);
  const editable = existing && existing.status === 'draft';
  const base = editable
    ? JSON.parse(JSON.stringify(existing))
    : makeEstimateDraft(truck, settings, { termsDays });

  if (!base.lines || base.lines.length === 0) {
    base.lines = [{ ...emptyEstimateLine(), partLineIds: [] }];
  }

  const line0 = base.lines[0];
  // Fill the three invoice fields only where the writer hasn't already put
  // something better than what a voice note produced.
  if (complaint  && !line0.complaint)  line0.complaint  = complaint;
  if (cause      && !line0.cause)      line0.cause      = cause;
  if (correction && !line0.correction) line0.correction = correction;

  line0.laborLines  = [...(line0.laborLines || []), ...laborLines];
  line0.partLineIds = [...new Set([...(line0.partLineIds || []), ...newParts.map(p => p.id)])];

  // Price against the parts list the caller is about to save, not the stale one
  // on `truck` — the new parts aren't on the record yet.
  const totals = calcTotals(base, [...(truck.parts || []), ...newParts]);

  return {
    ...base,
    subtotal:  totals.subtotal,
    tax:       totals.tax,
    total:     totals.total,
    updatedAt: nowSeconds(),
  };
}
